import React, { FC } from "react";
import { IProductCardType, IDisplayedField } from "./IProductCardOptionType";

interface IProps {
  product: IProductCardType;
  displayedFields: IDisplayedField[];
  checked: boolean;
  onChange: (value: string) => void;
  name: string;
  disabled?: boolean;
  register?: () => void;
}

const ProductCardChoiceItem: FC<IProps> = ({
  product,
  displayedFields,
  onChange,
  name,
  disabled,
  checked,
  register
}) => {
  function handleClick(event: React.MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    if (!disabled) {
      onChange(product.code);
    }
  }

  function hexToHsl(hex: string): { h: number; s: number; l: number } {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;

    if (max === min) return { h: 0, s: 0, l };

    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h = 0;
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;

    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100),
    };
  }

  function getDisplayValue(key: string): string | null {
    switch (key) {
      case "unit_price":
        return `${product.unit_price} \u20AC`;
      case "distribution_rate":
        return product.distribution_rate != null
          ? `${product.distribution_rate} %`
          : null;
      case "variable_capital":
        return product.variable_capital != null
          ? product.variable_capital
            ? "Variable"
            : "Fixe"
          : null;
      default:
        return product[key] != null ? String(product[key]) : null;
    }
  }

  const cardStyle = (() => {
    if (!product.color) return {};
    const { h, s } = hexToHsl(product.color);
    return {
      "--product-color": product.color,
      "--product-color-light": `hsl(${h}, ${s}%, 97%)`,
      "--product-color-border": `hsl(${h}, ${s}%, 80%)`,
    } as React.CSSProperties;
  })();

  return (
    <div className="col-md-6">
      <label
        htmlFor={`${name}_${product.code}`}
        className={`product-card-choice ${checked ? "product-card-choice--selected" : ""} ${disabled ? "product-card-choice--disabled" : ""}`}
        style={cardStyle}
        onClick={handleClick}
      >
        <input
          type="radio"
          readOnly
          checked={checked}
          name={name}
          value={product.code}
          disabled={disabled}
          id={`${name}_${product.code}`}
          ref={register}
        />

        <div className="product-card-choice__top">
          <div className="product-card-choice__header">
            {product.name}
            <span className="product-card-choice__radio" />
          </div>

          <p className="product-card-choice__title">{product.badge_label}</p>

          {product.tagline && (
            <p className="product-card-choice__description">
              {product.tagline}
            </p>
          )}
        </div>

        <div className="product-card-choice__divider"></div>

        <div className="product-card-choice__bottom">
          {displayedFields.length > 0 && (
            <div className="product-card-choice__stats">
              {displayedFields.map((field) => {
                const displayValue = getDisplayValue(field.key);
                if (displayValue == null) return null;
                return (
                  <div key={field.key} className="product-card-choice__stat">
                    <span className="product-card-choice__stat-label">
                      {field.label}
                    </span>
                    <span className="product-card-choice__stat-value">
                      {displayValue}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {product.tags && product.tags.length > 0 && (
            <div className="product-card-choice__tags">
              {product.tags.map((tag, index) => (
                <span key={index} className="product-card-choice__tag">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </label>
    </div>
  );
};

export default ProductCardChoiceItem;
